import { FC } from "react";

import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useAIFeaturesEnabled } from "@/features/LiveFormEditor/Steps/helpers/useAIFeaturesEnabled";
import { DonationStepDescriptionAiWizard } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionAiWizard/DonationStepDescriptionAiWizard";
import { useAiDescriptionWizardExperiment } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionAiWizard/helpers/useAiDescriptionWizardExperiment";
import { useLogAiDescriptionWizardStepViewed } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionAiWizard/helpers/useLogAiDescriptionWizardStepViewed";
import { DonationStepDescriptionPoweredByAI } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionPoweredByAI/DonationStepDescriptionPoweredByAI";
import { TicketingStepDescriptionPoweredByAI } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionPoweredByAI/TicketingStepDescriptionPoweredByAI";
import { DonationLiveEditorStepDescription } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionSimple/DonationLiveEditorStepDescription";
import { TicketingLiveEditorStepDescription } from "@/features/LiveFormEditor/Steps/StepDescription/StepDescriptionSimple/TicketingLiveEditorStepDescription";

export const StepDescriptionSelector: FC = () => {
  const { stepProgress, formType, editOrCreateMode, getIndexStepFromKey, isOnboarding } = useLiveFormEditorContext();

  const { isAIFeaturesEnabled } = useAIFeaturesEnabled();
  const experiment = useAiDescriptionWizardExperiment();
  const { isWizardEnabled, loading: isWizardExperimentLoading } = experiment;

  // The legacy AI flow still exits on save; Magic exits only after reaching a later step.
  const hasAlreadySavedStepDescription = getIndexStepFromKey("description") <= stepProgress.saved;

  // Determine if AI-powered description should be shown based on onboarding state and feature flags
  const shouldShowAIPoweredDescription =
    isAIFeaturesEnabled && !hasAlreadySavedStepDescription && editOrCreateMode === "create";

  const isAiDescriptionWizardEligible =
    isAIFeaturesEnabled && editOrCreateMode === "create" && formType === "donation-form" && !isOnboarding;
  const hasPassedDescription = stepProgress.reached > getIndexStepFromKey("description");

  useLogAiDescriptionWizardStepViewed({
    isEligible:
      isAiDescriptionWizardEligible && (isWizardEnabled ? !hasPassedDescription : shouldShowAIPoweredDescription),
    experiment,
  });

  if (formType === "ticketing") {
    if (shouldShowAIPoweredDescription) {
      return <TicketingStepDescriptionPoweredByAI />;
    }

    return <TicketingLiveEditorStepDescription />;
  }

  if (isAiDescriptionWizardEligible && isWizardExperimentLoading) {
    return null;
  }

  if (isAiDescriptionWizardEligible && isWizardEnabled) {
    return hasPassedDescription ? <DonationLiveEditorStepDescription /> : <DonationStepDescriptionAiWizard />;
  }

  if (shouldShowAIPoweredDescription) {
    return <DonationStepDescriptionPoweredByAI />;
  }

  return <DonationLiveEditorStepDescription />;
};
